import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const ThemeContext = createContext(null);

const STORAGE_KEY = "mt.theme.v1";

const defaultState = {
  mode: "light", // "dark" | "light" — first-time visitors open in light mode
  reduceMotion: false,
  fontSize: "medium", // "small" | "medium" | "large"
  language: "en",
};

export function ThemeProvider({ children }) {
  const [state, setState] = useState(() => {
    if (typeof window === "undefined") return defaultState;
    try {
      const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "null");
      if (saved && typeof saved === "object") return { ...defaultState, ...saved };
    } catch (_) { /* ignore */ }
    return defaultState;
  });

  // Apply state to <html>
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", state.mode === "dark");
    root.classList.toggle("reduce-motion", !!state.reduceMotion);

    // Font size
    const sizeMap = { small: "15px", medium: "16px", large: "18px" };
    root.style.fontSize = sizeMap[state.fontSize] || "16px";

    // Cursor toggle: glass cursor only when motion not reduced
    root.classList.toggle("glass-cursor-on", !state.reduceMotion);

    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (_) { /* ignore */ }
  }, [state]);

  const setMode = useCallback((mode) => setState((s) => ({ ...s, mode })), []);
  const toggleMode = useCallback(() => setState((s) => ({ ...s, mode: s.mode === "dark" ? "light" : "dark" })), []);
  const setReduceMotion = useCallback((reduceMotion) => setState((s) => ({ ...s, reduceMotion })), []);
  const setFontSize = useCallback((fontSize) => setState((s) => ({ ...s, fontSize })), []);
  const setLanguage = useCallback((language) => setState((s) => ({ ...s, language })), []);
  const reset = useCallback(() => setState(defaultState), []);

  return (
    <ThemeContext.Provider value={{ ...state, setMode, toggleMode, setReduceMotion, setFontSize, setLanguage, reset }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");
  return ctx;
}
